import { Visibility, VisibilityOff } from "@mui/icons-material";
import LoginIcon from "@mui/icons-material/Login";
import {
  FormControl,
  IconButton,
  InputAdornment,
  InputBase,
} from "@mui/material";
import axios from "axios";
import { useFormik } from "formik";
import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { useAuthContext } from "../../context/AuthProvider";
import { BASEURL } from "../../service/http.service";

const validationSchema = yup.object({
  Email: yup
    .string("Enter your email")
    .required("Email is required")
    .email("Email is not valid"),
  Password: yup
    .string("Enter your password")
    .matches(
      /^.*(?=.{8,})((?=.*[!@#$%^&*()\-_=+{};:,<.>]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/,
      "Password must contain at least 8 characters, one uppercase, one number and one special case character"
    )
    .required("Password is required"),
});

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
  },
  title: {
    fontSize: "25px",
    fontWeight: 500,
    color: "#404040",
  },
  error: {
    color: "red",
  },
  formBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "20px",
  },
  forgotPassword: {
    textDecoration: "underline",
    fontSize: "13px",
    fontWeight: 500,
    color: "#9F9F9F",
    cursor: "pointer",
  },
  signInButton: {
    color: "white",
    backgroundColor: "#0077B6",
    borderRadius: "20px",
    height: "30px",
    width: "100px",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "5px",
  },
};

function SignIn({ setIsLoginView }) {
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const { login, setAuthState } = useAuthContext();
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      Email: "",
      Password: "",
    },

    validationSchema: validationSchema,
    onSubmit: (values) => {
      handleFormSubmit(values);
    },
  });

  const handleFormSubmit = async () => {
    try {
      toast.loading("Signing In...");
      const response = await axios.post(`${BASEURL}/api/v1/users/login`, {
        email: formik.values.Email,
        password: formik.values.Password,
      });
      const tokens = response.data.data;
      const { accessToken, refreshToken } = tokens;
      localStorage.setItem("accessToken", accessToken);
      Cookies.set("refreshToken", refreshToken, {
        path: "/",
      });
      Cookies.set("authToken", response.data.data?.user?.role, {
        path: "/",
      });
      // console.log("refresh",refreshToken)
      login();

      localStorage.setItem(
        "role",
        JSON.stringify({
          isLoggedIn: true,
        })
      );
      setAuthState({
        isLoggedIn: true,
        accessToken,
        userInfo: response.data.data?.user,
        selectedRefinary: "Cottondale",
      });
      toast.dismiss();
      if (response.data.data?.user?.role === "Operator") {
        navigate("/plant");
      } else if (response.data.data?.user?.role === "Manager") {
        navigate("/plant");
      }
      toast.success(response.data.message || "Login successful");
    } catch (error) {
      toast.dismiss();
      console.error(
        "Login error",
        error.response?.data || "Something went wrong"
      );
      toast.error(error.response?.data?.error?.message || "Failed to login.");
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  useEffect(() => {
    setPassword(formik.values.Password);
  }, [formik.values.Password]);

  useEffect(() => {
    // Check if browser autofills the input fields
    if (formik.values.Email && formik.values.Password) {
      formik.setFieldTouched("Email", true);
      formik.setFieldTouched("Password", true);
    }
  }, [formik.values.Email, formik.values.Password]);

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    formik.setFieldValue("Password", e.target.value);
  };

  return (
    <div style={styles.container}>
      <div style={styles.title}>Sign In</div>
      <form onSubmit={formik.handleSubmit}>
        <div className="form-group">
          <div className="label">Email Address</div>
          <FormControl fullWidth>
            <InputBase
              fullWidth
              type="text"
              className="input-border"
              placeholder="enter email address"
              name="Email"
              value={formik.values.Email}
              onChange={formik.handleChange}
              variant="standard"
            />
            {formik.touched.Email && Boolean(formik.errors.Email) ? (
              <p style={{ color: "red" }}>
                {formik.touched.Email && formik.errors.Email}
              </p>
            ) : null}
          </FormControl>
        </div>
        <div className="form-group">
          <div className="label">Password</div>
          <FormControl fullWidth>
            <InputBase
              fullWidth
              type={showPassword ? "text" : "password"}
              placeholder="enter password"
              name="Password"
              className="input-border"
              value={password}
              onChange={handlePasswordChange}
              variant="standard"
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              key={showPassword ? "visible" : "hidden"}
              autoComplete="off"
            />
            {formik.touched.Password && Boolean(formik.errors.Password) ? (
              <p style={styles.error}>
                {formik.touched.Password && formik.errors.Password}
              </p>
            ) : null}
          </FormControl>
        </div>

        <div className="form-btn" style={styles.formBtn}>
          <div
            style={styles.forgotPassword}
            onClick={(e) => setIsLoginView(false)}
            onMouseEnter={(e) => (e.target.style.color = "#FFA500")}
            onMouseLeave={(e) => (e.target.style.color = "#9F9F9F")}
          >
            Forgot Password
          </div>
          <button
            type="submit"
            style={{
              ...styles.signInButton,
              backgroundColor:
                !formik.values.Email || !formik.values.Password
                  ? "lightgrey"
                  : "#0077B6",
              cursor:
                !formik.values.Email || !formik.values.Password
                  ? "not-allowed"
                  : "pointer",
            }}
            disabled={!formik.values.Email || !formik.values.Password}
          >
            <LoginIcon
              sx={{ width: "20px", height: "20px", color: "#FFFFFF" }}
            />
            Sign In
          </button>
        </div>
      </form>
      <Toaster />
    </div>
  );
}

export default SignIn;
